import React, { useState } from "react";
import {
    Box, Typography, IconButton, Chip, Avatar, Collapse,
} from "@mui/material";
import CenteredDetailModal from "./CenteredDetailModal";
import CloseIcon          from "@mui/icons-material/Close";
import PersonIcon         from "@mui/icons-material/Person";
import EmailIcon          from "@mui/icons-material/Email";
import CalendarTodayIcon  from "@mui/icons-material/CalendarToday";
import LockIcon           from "@mui/icons-material/Lock";
import AccessTimeIcon     from "@mui/icons-material/AccessTime";
import SchoolIcon         from "@mui/icons-material/School";
import FlightIcon         from "@mui/icons-material/Flight";
import TimerIcon          from "@mui/icons-material/Timer";
import ExpandMoreIcon     from "@mui/icons-material/ExpandMore";
import ExpandLessIcon     from "@mui/icons-material/ExpandLess";
import moment from "moment";

export const InfoRow = ({ icon, label, value, chip, chipColor, mono }) => (
    <Box sx={{ display: "flex", alignItems: "flex-start", gap: 2, py: 1.25, borderBottom: "1px solid rgba(26,26,26,0.06)" }}>
        <Box sx={{ color: "#c01e2e", mt: 0.5, flexShrink: 0 }}>{icon}</Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography sx={{ color: "#aaa", fontSize: "11px", mb: 0.4, fontFamily: "'Rubik', sans-serif", textTransform: "uppercase", letterSpacing: "0.3px" }}>
                {label}
            </Typography>
            {chip ? (
                <Chip label={value} size="small" color={chipColor || "default"} sx={{ borderRadius: "6px", fontWeight: 500, fontSize: "12px" }} />
            ) : (
                <Typography sx={{ color: "#1a1a1a", fontSize: mono ? "12px" : "13px", wordBreak: "break-all", fontFamily: mono ? "monospace" : "'Rubik', sans-serif" }}>
                    {value || "—"}
                </Typography>
            )}
        </Box>
    </Box>
);

export const Section = ({ title, children, collapsible, defaultOpen = true }) => {
    const [open, setOpen] = useState(defaultOpen);
    return (
        <Box sx={{ mb: 2.5 }}>
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", cursor: collapsible ? "pointer" : "default", mb: 1, py: 0.5 }}
                onClick={() => collapsible && setOpen(!open)}>
                <Typography sx={{ color: "#c01e2e", fontSize: "11px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.6px", fontFamily: "'Rubik', sans-serif" }}>
                    {title}
                </Typography>
                {collapsible && (open ? <ExpandLessIcon sx={{ color: "#aaa", fontSize: 18 }} /> : <ExpandMoreIcon sx={{ color: "#aaa", fontSize: 18 }} />)}
            </Box>
            <Collapse in={!collapsible || open}>
                <Box sx={{ backgroundColor: "#F7F4EF", borderRadius: "10px", p: "4px 12px 4px", border: "1px solid rgba(26,26,26,0.07)" }}>
                    {children}
                </Box>
            </Collapse>
        </Box>
    );
};

export default function StudentDetailDrawer({ open, onClose, student, school }) {
    if (!student) return null;

    const createdAt     = student.createdAt ? moment(student.createdAt).format("DD MMM YYYY, HH:mm") : null;
    const lastLogin     = (student.lastLogin || student.last_login) ? moment(student.lastLogin || student.last_login).format("DD MMM YYYY, HH:mm") : null;
    const joinedSchoolAt = (student.joinedAt || student.createdAt) ? moment(student.joinedAt || student.createdAt).format("DD MMM YYYY") : null;

    const getStatusLabel = () => student.status === "suspended" ? "Suspended" : student.status === "pending" ? "Pending" : "Active";
    const getStatusColor = () => student.status === "suspended" ? "warning" : student.status === "pending" ? "info" : "success";

    return (
        <CenteredDetailModal open={open} onClose={onClose} maxWidth={540}>

            {/* Header */}
            <Box sx={{ p: 3, borderBottom: "1px solid rgba(26,26,26,0.09)", display: "flex", alignItems: "center", justifyContent: "space-between", backgroundColor: "#ffffff" }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar sx={{ width: 52, height: 52, backgroundColor: school?.branding?.primary_color || "#c01e2e", fontSize: "18px" }}>
                        {student.first_name?.[0]}{student.last_name?.[0]}
                    </Avatar>
                    <Box>
                        <Typography sx={{ color: "#1a1a1a", fontSize: "17px", fontWeight: 600, fontFamily: "'Rubik', sans-serif" }}>
                            {student.first_name} {student.last_name}
                        </Typography>
                        <Chip label={getStatusLabel()} size="small" color={getStatusColor()}
                            sx={{ mt: 0.5, borderRadius: "6px", fontSize: "11px", fontWeight: 500 }} />
                    </Box>
                </Box>
                <IconButton onClick={onClose} sx={{ color: "#aaa", "&:hover": { color: "#1a1a1a" } }}>
                    <CloseIcon />
                </IconButton>
            </Box>

            {/* Content */}
            <Box sx={{ p: 3, overflowY: "auto", flex: 1, backgroundColor: "#FAF7F2" }}>
                <Section title="Contact Information">
                    <InfoRow icon={<EmailIcon fontSize="small" />} label="Email" value={student.email} />
                </Section>

                <Section title="Account Details">
                    <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Account Created" value={createdAt} />
                    <InfoRow icon={<AccessTimeIcon fontSize="small" />} label="Last Login" value={lastLogin || "Never"} />
                    <InfoRow icon={<PersonIcon fontSize="small" />} label="Status" value={getStatusLabel()} chip chipColor={getStatusColor()} />
                </Section>

                <Section title="Flight School">
                    <InfoRow icon={<SchoolIcon fontSize="small" />} label="School Name" value={school?.name || student.schoolName || "—"} />
                    <InfoRow icon={<CalendarTodayIcon fontSize="small" />} label="Joined Date" value={joinedSchoolAt || "—"} />
                </Section>

                <Section title="Usage Statistics">
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1.25, py: 1.5 }}>
                        <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
                            <Typography sx={{ color: "#c01e2e", fontSize: "22px", fontWeight: 700, fontFamily: "'Rubik', sans-serif" }}>{student.totalFlights || 0}</Typography>
                            <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Total Flights</Typography>
                        </Box>
                        <Box sx={{ textAlign: "center", p: 1.5, backgroundColor: "#ffffff", borderRadius: "10px", border: "1px solid rgba(26,26,26,0.09)" }}>
                            <Typography sx={{ color: "#c01e2e", fontSize: "22px", fontWeight: 700, fontFamily: "'Rubik', sans-serif" }}>{student.practiceHours || 0}h</Typography>
                            <Typography sx={{ color: "#aaa", fontSize: "11px", fontFamily: "'Rubik', sans-serif" }}>Practice Time</Typography>
                        </Box>
                    </Box>
                    <InfoRow icon={<FlightIcon fontSize="small" />} label="Completed Sessions" value={student.completedSessions || 0} />
                    <InfoRow icon={<TimerIcon fontSize="small" />} label="Avg Session Length" value={student.avgSessionLength ? `${student.avgSessionLength} min` : "—"} />
                </Section>

                <Section title="Internal IDs" collapsible defaultOpen={false}>
                    <InfoRow icon={<LockIcon fontSize="small" />} label="User ID" value={student._id} mono />
                </Section>
            </Box>
        </CenteredDetailModal>
    );
}
